import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import Header from './components/Header10';
import Footer from './components/Footer10';

const Checkout10 = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        cardNumber: '',
        cardName: '',
        expiryDate: '',
        cvv: '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const steps = [
        { number: 1, label: 'Shipping' },
        { number: 2, label: 'Payment' },
        { number: 3, label: 'Confirm' },
    ];

    const orderItems = [
        { name: 'Handwoven Cotton Saree', size: 'M', qty: 1, price: 4990 },
        { name: 'Linen Kurta Set', size: 'L', qty: 1, price: 3490 },
    ];

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.qty, 0);
    const shipping = 0;
    const total = subtotal + shipping;

    const inputClass = "w-full px-0 py-3 bg-transparent text-[#1C1917] text-sm font-light placeholder-[#A8A29E] focus:outline-none transition-colors duration-300";

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />

            <div style={{ fontFamily: "'Inter', sans-serif" }} className="min-h-screen bg-white">
                <Header />

                <main className="pt-16 md:pt-[72px]">
                    {/* Page Header */}
                    <div className="py-10 md:py-16 px-5 md:px-8 text-center" style={{ borderBottom: '1px solid #E7E5E4' }}>
                        <h1 className="text-2xl md:text-3xl font-light text-[#1C1917] tracking-tight">Checkout</h1>
                    </div>

                    {/* Steps */}
                    <div className="max-w-[600px] mx-auto px-5 md:px-8 py-8">
                        <div className="flex items-center justify-center gap-8 mb-12">
                            {steps.map((step, index) => (
                                <React.Fragment key={step.number}>
                                    <div className="flex items-center gap-2">
                                        <div
                                            className={`w-6 h-6 flex items-center justify-center text-[10px] transition-all duration-300 ${
                                                currentStep > step.number
                                                    ? 'bg-[#78716C] text-white'
                                                    : currentStep === step.number
                                                    ? 'text-[#1C1917]'
                                                    : 'text-[#D6D3D1]'
                                            }`}
                                            style={{
                                                border: currentStep >= step.number
                                                    ? '1px solid #78716C'
                                                    : '1px solid #E7E5E4',
                                            }}
                                        >
                                            {currentStep > step.number ? <Check className="w-3 h-3" strokeWidth={1.5} /> : step.number}
                                        </div>
                                        <span className={`text-xs font-light tracking-wider uppercase ${
                                            currentStep >= step.number ? 'text-[#1C1917]' : 'text-[#D6D3D1]'
                                        }`}>
                                            {step.label}
                                        </span>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`w-8 h-px ${currentStep > step.number ? 'bg-[#78716C]' : 'bg-[#E7E5E4]'}`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Step 1: Shipping */}
                        {currentStep === 1 && (
                            <div>
                                <p className="text-[#1C1917] text-xs tracking-[2px] uppercase mb-6">Shipping Address</p>
                                <div className="space-y-0">
                                    {[
                                        { name: 'fullName', placeholder: 'Full Name' },
                                        { name: 'email', placeholder: 'Email', type: 'email' },
                                        { name: 'phone', placeholder: 'Phone', type: 'tel' },
                                        { name: 'address', placeholder: 'Address' },
                                        { name: 'city', placeholder: 'City' },
                                        { name: 'state', placeholder: 'State' },
                                        { name: 'pincode', placeholder: 'Pincode' },
                                    ].map((field) => (
                                        <div key={field.name} style={{ borderBottom: '1px solid #E7E5E4' }}>
                                            <input
                                                type={field.type || 'text'}
                                                name={field.name}
                                                placeholder={field.placeholder}
                                                value={formData[field.name]}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                                style={{ fontFamily: "'Inter', sans-serif" }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <button
                                    onClick={() => setCurrentStep(2)}
                                    className="w-full mt-8 py-3.5 bg-[#78716C] text-white text-xs tracking-[2px] uppercase font-normal hover:bg-[#57534E] transition-colors duration-300 cursor-pointer border-none"
                                >
                                    Continue to Payment
                                </button>
                            </div>
                        )}

                        {/* Step 2: Payment */}
                        {currentStep === 2 && (
                            <div>
                                <p className="text-[#1C1917] text-xs tracking-[2px] uppercase mb-6">Payment Details</p>
                                <div className="space-y-0">
                                    {[
                                        { name: 'cardNumber', placeholder: 'Card Number' },
                                        { name: 'cardName', placeholder: 'Name on Card' },
                                        { name: 'expiryDate', placeholder: 'MM/YY' },
                                        { name: 'cvv', placeholder: 'CVV' },
                                    ].map((field) => (
                                        <div key={field.name} style={{ borderBottom: '1px solid #E7E5E4' }}>
                                            <input
                                                type="text"
                                                name={field.name}
                                                placeholder={field.placeholder}
                                                value={formData[field.name]}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                                style={{ fontFamily: "'Inter', sans-serif" }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-3 mt-8">
                                    <button
                                        onClick={() => setCurrentStep(1)}
                                        className="flex-1 py-3.5 text-[#78716C] text-xs tracking-[2px] uppercase font-normal hover:text-[#1C1917] transition-colors duration-300 cursor-pointer bg-transparent"
                                        style={{ border: '1px solid #E7E5E4' }}
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={() => setCurrentStep(3)}
                                        className="flex-1 py-3.5 bg-[#78716C] text-white text-xs tracking-[2px] uppercase font-normal hover:bg-[#57534E] transition-colors duration-300 cursor-pointer border-none"
                                    >
                                        Review Order
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Confirm */}
                        {currentStep === 3 && (
                            <div>
                                <p className="text-[#1C1917] text-xs tracking-[2px] uppercase mb-6">Order Summary</p>

                                <div className="space-y-4 mb-8">
                                    {orderItems.map((item, index) => (
                                        <div key={index} className="flex justify-between items-center py-3" style={{ borderBottom: '1px solid #E7E5E4' }}>
                                            <div>
                                                <p className="text-[#1C1917] text-sm font-light">{item.name}</p>
                                                <p className="text-[#A8A29E] text-xs font-light">Size: {item.size} | Qty: {item.qty}</p>
                                            </div>
                                            <span className="text-[#78716C] text-sm font-light">₹{item.price.toLocaleString('en-IN')}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="space-y-3 mb-8">
                                    <div className="flex justify-between">
                                        <span className="text-[#78716C] text-sm font-light">Subtotal</span>
                                        <span className="text-[#1C1917] text-sm font-light">₹{subtotal.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-[#78716C] text-sm font-light">Shipping</span>
                                        <span className="text-[#1C1917] text-sm font-light">Free</span>
                                    </div>
                                    <div className="w-full h-px bg-[#E7E5E4]" />
                                    <div className="flex justify-between">
                                        <span className="text-[#1C1917] text-sm font-normal">Total</span>
                                        <span className="text-[#1C1917] text-lg font-light">₹{total.toLocaleString('en-IN')}</span>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setCurrentStep(2)}
                                        className="flex-1 py-3.5 text-[#78716C] text-xs tracking-[2px] uppercase font-normal hover:text-[#1C1917] transition-colors duration-300 cursor-pointer bg-transparent"
                                        style={{ border: '1px solid #E7E5E4' }}
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={() => navigate('/template10/orders')}
                                        className="flex-1 py-3.5 bg-[#78716C] text-white text-xs tracking-[2px] uppercase font-normal hover:bg-[#57534E] transition-colors duration-300 cursor-pointer border-none"
                                    >
                                        Place Order
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Checkout10;
